import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { usePageConfig } from '@/contexts/PageConfigContext';
import { useBreakpointDown } from '@/hooks/useBreakpoint';
import { apiClient } from '@/services/api';
import { Card } from '@/components/layout';
import { ProfileAvatar, ProfileInfo, UserEditForm } from '@/widgets';
import { ContentLayout } from '@/layouts/ContentLayout';
import { AccountLayoutContext } from './context';
import type { AccountLayoutContextType, ProfileInfoItem } from './context';
import EditTab from '@/pages/account/EditTab';
import MessagesTab from '@/pages/account/MessagesTab';
import SecurityTab from '@/pages/account/SecurityTab';

const AccountLayout: React.FC = () => {
  const { t } = useTranslation(['account']);
  const { user, setUser } = useAuth();
  const isMobile = useBreakpointDown('lg');

  const [showEditForm, setShowEditForm] = useState(false);

  usePageConfig({
    title: t('account:accountSettings'),
    headerSolid: true,
  });

  const handleAvatarChange = useCallback(async (file: File) => {
    try {
      const response = await apiClient.uploadAvatar(file);
      if (response.data?.success && user) {
        setUser({ ...user, avatar_url: response.data.data.avatar_url });
      }
    } catch (err: any) {
      console.error('Avatar upload error:', err);
    }
  }, [user, setUser]);

  const handleAvatarRemove = useCallback(async () => {
    try {
      const response = await apiClient.removeAvatar();
      if (response.data?.success && user) {
        setUser({ ...user, avatar_url: undefined });
      }
    } catch (err: any) {
      console.error('Avatar remove error:', err);
    }
  }, [user, setUser]);

  const profileInfoItems = useMemo<ProfileInfoItem[]>(() => (user ? [
    { label: `${t('account:firstName')} / ${t('account:lastName')}`, value: `${user.first_name || ''} ${user.last_name || ''}`.trim() || '-' },
    { label: t('account:phone'), value: user.phone || '-' },
    { label: t('account:emailLabel'), value: user.email || '-' },
    { label: t('account:country'), value: '-' },
  ] : []), [user, t]);

  const sidebar = (
    <>
      <ProfileAvatar
        name={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User'}
        location={undefined}
        avatarUrl={user?.avatar_url}
        onAvatarChange={handleAvatarChange}
        onAvatarRemove={handleAvatarRemove}
      />
      <ProfileInfo items={profileInfoItems} />
    </>
  );

  // Mobile overlay: when edit form is shown, replace swiper tabs entirely
  const mobileOverlay = showEditForm ? (
    <Card>
      <UserEditForm
        onSuccess={() => setShowEditForm(false)}
        onCancel={() => setShowEditForm(false)}
        onClose={() => setShowEditForm(false)}
      />
    </Card>
  ) : undefined;

  const tabs = useMemo(() => [
    { component: EditTab, path: '/account/edit' },
    { component: MessagesTab, path: '/account/messages' },
    { component: SecurityTab, path: '/account/security' },
  ], []);

  const contextValue: AccountLayoutContextType = useMemo(() => ({
    user,
    setUser,
    showEditForm,
    setShowEditForm,
    handleAvatarChange,
    handleAvatarRemove,
    profileInfoItems,
    isMobile,
  }), [user, setUser, showEditForm, handleAvatarChange, handleAvatarRemove, profileInfoItems, isMobile]);

  return (
    <AccountLayoutContext.Provider value={contextValue}>
      <ContentLayout
        sidebar={sidebar}
        tabs={tabs}
        mobileOverlay={mobileOverlay}
      />
    </AccountLayoutContext.Provider>
  );
};

export default AccountLayout;
